import { useEffect, useState } from "react";
import { FCC } from "@/types/common";
import { decode } from "tiff";

type TTIFFPreview = {
  file?: File;
  url?: string;
  width?: number;
  height?: number;
};

/**
 * TIFFPreview - Renders TIFF images by decoding them to PNG via canvas
 * Supports both File objects (for local uploads) and URLs (for server files)
 */
export const TIFFPreview: FCC<TTIFFPreview> = ({ file, url, width = 800, height = 500 }) => {
  const [imgDataUrl, setImgDataUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!file && !url) return;

    let isCancelled = false;

    const loadTIFF = async () => {
      try {
        setError(null);

        let arrayBuffer: ArrayBuffer;

        if (file) {
          arrayBuffer = await file.arrayBuffer();
        } else if (url) {
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`Failed to fetch TIFF: ${response.statusText}`);
          }
          arrayBuffer = await response.arrayBuffer();
        } else {
          return;
        }

        const tiffData = decode(new Uint8Array(arrayBuffer));

        if (isCancelled) return;

        const ifd = tiffData[0];
        if (!ifd) {
          throw new Error("No image data found in TIFF");
        }

        const { width, height, data } = ifd;

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          throw new Error("Failed to get canvas context");
        }

        const bytesPerPixel = data.length / (width * height);
        const imageData = ctx.createImageData(width, height);
        const pixels = imageData.data;

        if (bytesPerPixel === 4) {
          pixels.set(data);
        } else if (bytesPerPixel === 3) {
          for (let i = 0, j = 0; i < data.length; i += 3, j += 4) {
            pixels[j] = data[i]; // R
            pixels[j + 1] = data[i + 1]; // G
            pixels[j + 2] = data[i + 2]; // B
            pixels[j + 3] = 255; // A
          }
        } else if (bytesPerPixel === 1) {
          for (let i = 0, j = 0; i < data.length; i++, j += 4) {
            const gray = data[i];
            pixels[j] = gray; // R
            pixels[j + 1] = gray; // G
            pixels[j + 2] = gray; // B
            pixels[j + 3] = 255; // A
          }
        } else {
          throw new Error(`Unsupported pixel format: ${bytesPerPixel} bytes per pixel`);
        }

        ctx.putImageData(imageData, 0, 0);

        if (!isCancelled) {
          setImgDataUrl(canvas.toDataURL("image/png"));
        }
      } catch (err) {
        if (!isCancelled) {
          console.error("TIFF decode failed:", err);
          setError(err instanceof Error ? err.message : "Failed to load TIFF");
        }
      }
    };

    loadTIFF();

    return () => {
      isCancelled = true;
      setImgDataUrl(null);
      setError(null);
    };
  }, [file, url]);

  if (error) {
    return <div className="text-red-500 p-4 border border-red-300 rounded">Error loading TIFF: {error}</div>;
  }

  if (!imgDataUrl) {
    return <div className="text-gray-500" style={{ width: width, height: height }}></div>;
  }

  return <img src={imgDataUrl} alt="TIFF preview" style={{ width: width, height: height }} />;
};
